/** Shared lookups and checks for intent definitions. */
import type { LayerId, PaintInput, Placement, RasterFillInput, Ref, RejectionCode, StrokeInput } from '@maipo/agent-protocol';
import type { FillDefinition } from '../../core/commands';
import { findLayer, findLayerParent, type EditorDocument, type GroupLayer, type Layer } from '../../core/model';
import type { SolidPaint, VectorStroke } from '../../vector/types';
import { IntentRejection, type ActorPolicy, type Diagnostic, type ValidateContext } from '../registry';

export function isAlias(ref: Ref): boolean {
  return ref.startsWith('$');
}

/** Resolves a Ref to a layer ID, or undefined when it is an unresolved alias. */
export function resolveRef(ref: Ref, aliases: ReadonlyMap<string, LayerId>): LayerId | undefined {
  return isAlias(ref) ? aliases.get(ref) : ref;
}

/** Layer IDs a footprint can name statically (aliases created in this transaction are new and cannot conflict). */
export function staticIds(refs: readonly Ref[], aliases: ReadonlyMap<string, LayerId>): LayerId[] {
  return refs.map((ref) => resolveRef(ref, aliases)).filter((id): id is LayerId => id !== undefined);
}

export type Lookup = { state: 'found'; layer: Layer } | { state: 'pending' } | { state: 'missing' };

export function lookup(document: EditorDocument, ref: Ref, context: Pick<ValidateContext, 'aliases' | 'pending'>): Lookup {
  if (isAlias(ref)) {
    const id = context.aliases.get(ref);
    if (id === undefined) return context.pending.has(ref) ? { state: 'pending' } : { state: 'missing' };
    const layer = findLayer(document.root, id);
    return layer ? { state: 'found', layer } : { state: 'missing' };
  }
  const layer = findLayer(document.root, ref);
  return layer ? { state: 'found', layer } : { state: 'missing' };
}

export function error(code: RejectionCode, message: string, layer?: LayerId): Diagnostic {
  return { severity: 'error', code, message, ...(layer === undefined ? {} : { layer }) };
}

/** Finds a layer for validation; records LAYER_NOT_FOUND and returns null when missing or not yet created. */
export function layerFor(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[]): Layer | null {
  const found = lookup(document, ref, context);
  if (found.state === 'found') return found.layer;
  if (found.state === 'missing') diagnostics.push(error('LAYER_NOT_FOUND', `Layer ${JSON.stringify(ref).slice(0, 80)} does not exist.`, isAlias(ref) ? undefined : ref));
  return null;
}

/** Layer for compile time; aliases are resolved by then. */
export function requireLayer(document: EditorDocument, ref: Ref, aliases: ReadonlyMap<string, LayerId>): Layer {
  const id = resolveRef(ref, aliases);
  const layer = id === undefined ? undefined : findLayer(document.root, id);
  if (!layer) throw new IntentRejection('LAYER_NOT_FOUND', `Layer ${JSON.stringify(ref).slice(0, 80)} does not exist.`, id);
  return layer;
}

/** Non-root ancestors, nearest first. */
export function ancestors(document: EditorDocument, id: LayerId): GroupLayer[] {
  const trail: GroupLayer[] = [];
  const visit = (group: GroupLayer, path: GroupLayer[]): boolean => {
    for (const child of group.children) {
      if (child.id === id) { trail.push(...[...path].reverse()); return true; }
      if (child.kind === 'group' && visit(child, [...path, child])) return true;
    }
    return false;
  };
  visit(document.root, []);
  return trail;
}

export function ancestorIds(document: EditorDocument, id: LayerId): LayerId[] {
  return ancestors(document, id).map((group) => group.id);
}

export function parentKey(document: EditorDocument, id: LayerId): LayerId {
  const location = findLayerParent(document.root, id);
  return !location || location.parent === document.root ? 'root' : location.parent.id;
}

export type LockMode = 'any' | 'position' | 'pixels';

/**
 * Locks are the user's "do not touch" tool (ADR-009 §5.2). They bind actors
 * whose policy enforces locks (agents, add-ons); for the user the dispatcher
 * leaves locks to the native commands, exactly as the editor UI always has. `locked` on the
 * layer or any ancestor blocks every edit; `positionLocked` blocks moves and
 * transforms; `pixelsLocked` blocks pixel edits.
 */
export function lockingLayer(document: EditorDocument, layer: Layer, mode: LockMode): Layer | null {
  if (layer.locked) return layer;
  if (mode === 'position' && layer.positionLocked) return layer;
  if (mode === 'pixels' && layer.pixelsLocked) return layer;
  return ancestors(document, layer.id).find((ancestor) => ancestor.locked) ?? null;
}

export function checkUnlocked(document: EditorDocument, layer: Layer, mode: LockMode, diagnostics: Diagnostic[], policy: ActorPolicy): void {
  if (policy.locks === 'native') return;
  const locking = lockingLayer(document, layer, mode);
  if (!locking) return;
  const what = locking === layer ? 'is locked' : `is inside locked group ${locking.id}`;
  diagnostics.push(error('LAYER_LOCKED', `Layer ${layer.id} ${what}.`, layer.id));
}

/** A locked group (or locked ancestor) refuses changes to its child list. */
export function checkContainerUnlocked(document: EditorDocument, parent: GroupLayer, diagnostics: Diagnostic[], subject: LayerId, policy: ActorPolicy): void {
  if (policy.locks === 'native' || parent === document.root) return;
  const locking = parent.locked ? parent : ancestors(document, parent.id).find((ancestor) => ancestor.locked);
  if (locking) diagnostics.push(error('LAYER_LOCKED', `Group ${locking.id} is locked; its children cannot change.`, subject));
}

export function toSolidPaint(input: PaintInput): SolidPaint {
  return { type: 'solid', color: input.color.toLowerCase(), alpha: input.alpha ?? 1 };
}

export function toStroke(input: StrokeInput): VectorStroke {
  return { paint: toSolidPaint(input.paint), width: input.width, lineCap: input.lineCap ?? 'butt', lineJoin: input.lineJoin ?? 'miter', dash: [...(input.dash ?? [])] };
}

export function hexToRgba(color: string, alpha = 1): readonly [number, number, number, number] {
  const value = Number.parseInt(color.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, Math.round(Math.max(0, Math.min(1, alpha)) * 255)];
}

/** A protocol raster fill (hex colours, 0-1 alphas) as the native FillRasterCommand definition. */
export function rasterFillDefinition(fill: RasterFillInput): FillDefinition {
  if (fill.type === 'solid') return { type: 'solid', color: hexToRgba(fill.color, fill.alpha ?? 1) };
  return { type: 'linear-gradient', start: { ...fill.start }, end: { ...fill.end }, startColor: hexToRgba(fill.startColor, fill.startAlpha ?? 1), endColor: hexToRgba(fill.endColor, fill.endAlpha ?? 1) };
}

export interface ResolvedPlacement {
  parent: GroupLayer;
  /** Parent key for native commands: 'root' or a group id. */
  parentId: LayerId;
  /** Insertion index in the parent's current child list (before any removal). */
  index: number;
}

/**
 * Resolves a Placement against the current document. `moving` is the layer
 * being reordered (absent for new layers); its current position turns a final
 * `index` into the pre-removal index ReorderLayerCommand expects.
 */
export function resolvePlacement(document: EditorDocument, placement: Placement | undefined, aliases: ReadonlyMap<string, LayerId>, moving?: Layer): ResolvedPlacement {
  const keyOf = (group: GroupLayer) => group === document.root ? 'root' : group.id;
  if (placement && ('above' in placement || 'below' in placement)) {
    const ref = 'above' in placement ? placement.above : placement.below;
    const reference = requireLayer(document, ref, aliases);
    if (moving && reference.id === moving.id) throw new IntentRejection('COMMAND_REJECTED', 'A layer cannot be placed relative to itself.', moving.id);
    const location = findLayerParent(document.root, reference.id);
    if (!location) throw new IntentRejection('LAYER_NOT_FOUND', `Layer ${reference.id} has no parent.`, reference.id);
    return { parent: location.parent, parentId: keyOf(location.parent), index: location.index + ('above' in placement ? 0 : 1) };
  }
  const parentRef = placement && 'parent' in placement ? placement.parent : undefined;
  let parent: GroupLayer;
  if (parentRef === undefined) {
    parent = moving ? findLayerParent(document.root, moving.id)?.parent ?? document.root : document.root;
  } else if (parentRef === 'root') {
    parent = document.root;
  } else {
    const candidate = requireLayer(document, parentRef, aliases);
    if (candidate.kind !== 'group') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Placement parent ${candidate.id} is a ${candidate.kind} layer, not a group.`, candidate.id);
    parent = candidate;
  }
  const finalIndex = placement && 'index' in placement && placement.index !== undefined ? placement.index : 0;
  const current = moving ? parent.children.findIndex((child) => child.id === moving.id) : -1;
  const index = current >= 0 && finalIndex > current ? finalIndex + 1 : finalIndex;
  return { parent, parentId: keyOf(parent), index: Math.min(index, parent.children.length) };
}

/** Validation-time view of a placement: checks references and locks, tolerating pending aliases. */
export function validatePlacement(document: EditorDocument, placement: Placement | undefined, context: ValidateContext, diagnostics: Diagnostic[], subject?: Layer): void {
  if (placement && ('above' in placement || 'below' in placement)) {
    const ref = 'above' in placement ? placement.above : placement.below;
    const reference = layerFor(document, ref, context, diagnostics);
    if (!reference) return;
    if (subject && reference.id === subject.id) { diagnostics.push(error('COMMAND_REJECTED', 'A layer cannot be placed relative to itself.', subject.id)); return; }
    const location = findLayerParent(document.root, reference.id);
    if (location) checkContainerUnlocked(document, location.parent, diagnostics, subject?.id ?? reference.id, context.policy);
    if (subject?.kind === 'group' && location && (location.parent.id === subject.id || findLayer(subject, location.parent.id))) {
      diagnostics.push(error('COMMAND_REJECTED', 'A group cannot be moved into itself or one of its descendants.', subject.id));
    }
    return;
  }
  const parentRef = placement && 'parent' in placement ? placement.parent : undefined;
  if (parentRef === undefined || parentRef === 'root') return;
  const parent = layerFor(document, parentRef, context, diagnostics);
  if (!parent) return;
  if (parent.kind !== 'group') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Placement parent ${parent.id} is a ${parent.kind} layer, not a group.`, parent.id)); return; }
  checkContainerUnlocked(document, parent, diagnostics, subject?.id ?? parent.id, context.policy);
  if (subject?.kind === 'group' && (parent.id === subject.id || findLayer(subject, parent.id))) {
    diagnostics.push(error('COMMAND_REJECTED', 'A group cannot be moved into itself or one of its descendants.', subject.id));
  }
}

/** Parent keys a placement reads (for footprints). */
export function placementReads(document: EditorDocument, placement: Placement | undefined, aliases: ReadonlyMap<string, LayerId>, moving?: LayerId): LayerId[] {
  const reads: LayerId[] = [];
  if (moving) reads.push(parentKey(document, moving));
  if (placement && ('above' in placement || 'below' in placement)) {
    const id = resolveRef('above' in placement ? placement.above : placement.below, aliases);
    if (id !== undefined) { reads.push(id); if (findLayer(document.root, id)) reads.push(parentKey(document, id)); }
  } else {
    const parentRef = placement && 'parent' in placement ? placement.parent : undefined;
    if (parentRef === undefined) reads.push(moving ? parentKey(document, moving) : 'root');
    else if (parentRef === 'root') reads.push('root');
    else { const id = resolveRef(parentRef, aliases); if (id !== undefined) reads.push(id); }
  }
  return [...new Set(reads)];
}
